<template>
    <card>
        <b-row class="mb-3">
            <b-col cols="6">
                <h1 class="card-title">PDMP Management</h1>
            </b-col>
            <b-col cols="6" class="text-right">
                <b-button v-if="canConfigurePMP" variant="primary" size="sm" @click="openDefaultConfig">
                    Default Clearinghouse
                </b-button>
            </b-col>
        </b-row>

        <!-- Failure Alerts -->
        <div v-if="submissionFailedStates.length > 0 || authFailedStates.length > 0"
            class="pmp-alerts mb-3" :data-alert-count="alertCount">
            <b-alert v-if="submissionFailedStates.length > 0" show variant="danger"
                class="m-0 d-flex align-items-center clickable-alert" @click.native="filterBySubmissionFailures">
                <div class="alert-icon">
                    <i class="fas fa-exclamation-triangle" />
                </div>
                <div>
                    <strong>Submission Failures:</strong>
                    {{ submissionFailedStates.join(', ') }} — Review error details below.
                </div>
            </b-alert>
            <b-alert v-if="authFailedStates.length > 0" show variant="warning"
                class="m-0 d-flex align-items-center clickable-alert" @click.native="filterByAuthFailures">
                <div class="alert-icon">
                    <i class="fas fa-key" />
                </div>
                <div>
                    <strong>Credential Issues:</strong>
                    {{ authFailedStates.join(', ') }} — Update SFTP credentials to resolve.
                </div>
            </b-alert>
        </div>

        <!-- Filters -->
        <div class="filters">
            <b-form-group label="Frequency">
                <b-select v-model="filterFrequency" :options="frequencyOptions" size="sm" />
            </b-form-group>
            <b-form-group label="Last Result">
                <b-select v-model="filterLastResult" :options="lastResultOptions" size="sm" />
            </b-form-group>
            <b-form-group label="Config Type">
                <b-select v-model="filterConfigType" :options="configTypeOptions" size="sm" />
            </b-form-group>
            <b-form-group label="Status">
                <b-select v-model="filterStatus" :options="statusOptions" size="sm" />
            </b-form-group>
            <b-form-group label="Store">
                <b-select v-model="selectedStoreId" :options="storeOptions" size="sm"
                    @change="onStoreFilterChanged" />
            </b-form-group>
        </div>

        <!-- PMP Status Table -->
        <b-row>
            <b-col>
                <b-table id="pmpstatus-history-table" striped selectable hoverable responsive="true"
                    sort-icon-left :items="filteredStatus" :fields="pmpstatusFields" show-empty
                    @row-clicked="onRowClicked">
                    <template #cell(status)="data">
                        <b-badge v-if="data.value === 'Success'" variant="success">Success</b-badge>
                        <b-badge v-else-if="data.value === 'AuthenticationFailed'" variant="danger">
                            Auth Failed
                        </b-badge>
                        <b-badge v-else-if="data.value === 'ConnectionFailed'" variant="warning">
                            Connection Failed
                        </b-badge>
                        <b-badge v-else-if="data.value === 'Timeout'" variant="warning">Timeout</b-badge>
                        <b-badge v-else-if="data.value === 'Failed'" variant="danger">Failed</b-badge>
                        <b-badge v-else variant="secondary">Unknown</b-badge>
                    </template>
                    <template #cell(configSource)="data">
                        <b-badge v-if="data.value === 'Clearinghouse'" variant="info">Clearinghouse</b-badge>
                        <b-badge v-else-if="data.value === 'Scriptly'" variant="success">Scriptly</b-badge>
                        <b-badge v-else-if="data.value === 'Custom'" variant="primary">Custom</b-badge>
                        <b-badge v-else-if="data.value === 'Legacy'" variant="secondary">Legacy</b-badge>
                        <b-badge v-else variant="light">{{ data.value }}</b-badge>
                    </template>
                    <template #cell(isActive)="data">
                        <b-badge :variant="data.value ? 'success' : 'secondary'">
                            {{ data.value ? 'Enabled' : 'Disabled' }}
                        </b-badge>
                    </template>
                    <template #cell(actions)="data">
                        <b-button v-if="canConfigurePMP" size="sm" variant="primary"
                            @click.stop="editConfig(data.item)">
                            Configure
                        </b-button>
                        <b-button size="sm" variant="primary" class="ml-1"
                            @click.stop="viewHistory(data.item)">
                            History
                        </b-button>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <!-- SFTP Config Edit Modal -->
        <b-modal v-model="showConfigModal" :title="'SFTP Configuration: ' + editingState" size="lg" no-fade
            hide-footer>
            <b-form>
                <b-form-group label="Active" label-cols="3">
                    <b-form-checkbox v-model="configForm.isActive">
                        Enable submissions for this state
                    </b-form-checkbox>
                </b-form-group>
                <b-form-group label="Credential Source" label-cols="3">
                    <b-form-radio-group v-model="configForm.credentialSource" stacked>
                        <b-form-radio value="DefaultClearinghouse">
                            Default Clearinghouse
                            <HelpPopup :markdown="'Uses the shared Default Clearinghouse SFTP credentials configured at the top of this page. All states using this option share the same host, port, and login. Only the file location is state-specific.'" />
                        </b-form-radio>
                        <b-form-radio value="ScriptlyManaged">
                            Scriptly Managed
                            <HelpPopup :markdown="'Credentials are managed by Scriptly and stored securely. No manual SFTP configuration is needed — connection details are maintained centrally by the Scriptly team.'" />
                        </b-form-radio>
                        <b-form-radio value="Custom">
                            Custom SFTP Settings
                            <HelpPopup :markdown="'Provide state-specific SFTP connection details including host, port, authentication method, and credentials. Use this when the state\'s PDMP system requires unique connection settings.'" />
                        </b-form-radio>
                    </b-form-radio-group>
                </b-form-group>

                <template v-if="configForm.credentialSource === 'Custom'">
                    <hr />
                    <h6>Custom SFTP Settings</h6>
                    <b-form-group label="Host *" label-cols="3">
                        <b-form-input v-model="configForm.host" placeholder="e.g., sftp.example.com"
                            :state="configFieldState(configForm.host)" />
                        <b-form-invalid-feedback>Host is required.</b-form-invalid-feedback>
                    </b-form-group>
                    <b-form-group label="Port" label-cols="3">
                        <b-form-input v-model.number="configForm.port" type="number" />
                    </b-form-group>
                    <b-form-group label="Auth Type" label-cols="3">
                        <b-form-select v-model="configForm.authType" :options="authTypes" />
                    </b-form-group>
                    <b-form-group label="Username *" label-cols="3">
                        <b-form-input v-model="configForm.username"
                            :state="configFieldState(configForm.username)" />
                        <b-form-invalid-feedback>Username is required.</b-form-invalid-feedback>
                    </b-form-group>

                    <template v-if="configForm.authType === 'Password' || configForm.authType === 'KeyboardInteractive'">
                        <b-form-group label="Password" label-cols="3">
                            <b-form-input v-model="configForm.password" type="password"
                                :placeholder="editingConfigId ? '(unchanged)' : 'Enter password'" />
                        </b-form-group>
                    </template>

                    <template v-if="configForm.authType === 'PrivateKey'">
                        <b-form-group label="Private Key" label-cols="3">
                            <b-form-textarea v-model="configForm.privateKey" rows="4"
                                placeholder="Paste SSH private key content or upload below" />
                        </b-form-group>
                        <b-form-group label="Upload Key File" label-cols="3">
                            <b-form-file @input="onKeyFileSelected" accept=".pem,.key,.ppk"
                                placeholder="Choose a private key file..." />
                        </b-form-group>
                        <b-form-group label="Passphrase" label-cols="3">
                            <b-form-input v-model="configForm.passphrase" type="password"
                                :placeholder="editingConfigId ? '(unchanged)' : 'Enter passphrase (if required)'" />
                        </b-form-group>
                    </template>
                </template>

                <b-form-group label="File Location" label-cols="3">
                    <b-form-input v-model="configForm.fileLocation"
                        placeholder="Optional: file path for local delivery (e.g., NY)" />
                </b-form-group>

                <hr />
                <b-form-group label="Notes" label-cols="3">
                    <b-form-textarea v-model="configForm.notes" rows="3"
                        placeholder="e.g., credential expiration dates, state contacts, special requirements" />
                </b-form-group>

                <b-form-group v-if="configForm.credentialSource === 'Custom'" label-cols="3">
                    <b-button size="sm" variant="secondary" @click="testConnection" :disabled="testingConnection">
                        {{ testingConnection ? 'Testing...' : 'Test Connection' }}
                    </b-button>
                    <b-alert
                        v-if="testResult !== null"
                        show
                        :variant="testResult.success ? 'success' : 'danger'"
                        dismissible
                        class="mt-2 mb-0"
                        @dismissed="testResult = null"
                    >
                        {{ testResult.message }}
                    </b-alert>
                </b-form-group>

            </b-form>
            <div class="d-flex justify-content-between align-items-center mt-3">
                <b-link v-if="editingConfigId" @click="openSftpConfigHistory">
                    <i class="fa fa-history" /> Change History
                </b-link>
                <span v-else />
                <b-button variant="primary" @click="saveConfig" :disabled="savingConfig">Save</b-button>
            </div>
        </b-modal>

        <!-- Default Clearinghouse Config Modal -->
        <b-modal v-model="showDefaultConfig" title="Default Clearinghouse Configuration" size="lg" no-fade
            hide-footer>
            <b-form>
                <b-form-group label="Credential Source" label-cols="3">
                    <b-form-radio-group v-model="defaultConfigForm.credentialSource" stacked>
                        <b-form-radio value="ScriptlyManaged">
                            Scriptly Managed
                            <HelpPopup :markdown="'Credentials are managed by Scriptly and stored securely. No manual SFTP configuration is needed — connection details are maintained centrally by the Scriptly team.'" />
                        </b-form-radio>
                        <b-form-radio value="Custom">
                            Custom SFTP Settings
                            <HelpPopup :markdown="'Provide custom SFTP connection details including host, port, authentication method, and credentials. All states using the Default Clearinghouse will share these settings.'" />
                        </b-form-radio>
                    </b-form-radio-group>
                </b-form-group>

                <template v-if="defaultConfigForm.credentialSource === 'ScriptlyManaged'">
                    <b-alert show variant="info" class="mt-2">
                        Credentials are managed by the Scriptly platform. No manual configuration needed.
                    </b-alert>
                </template>

                <template v-if="defaultConfigForm.credentialSource === 'Custom'">
                    <hr />
                    <b-form-group label="Host *" label-cols="3">
                        <b-form-input v-model="defaultConfigForm.host" placeholder="e.g., submissions.healthcarecoordination.net"
                            :state="defaultConfigFieldState(defaultConfigForm.host)" />
                        <b-form-invalid-feedback>Host is required.</b-form-invalid-feedback>
                    </b-form-group>
                    <b-form-group label="Port" label-cols="3">
                        <b-form-input v-model.number="defaultConfigForm.port" type="number" />
                    </b-form-group>
                    <b-form-group label="Auth Type" label-cols="3">
                        <b-form-select v-model="defaultConfigForm.authType" :options="authTypes" />
                    </b-form-group>
                    <b-form-group label="Username *" label-cols="3">
                        <b-form-input v-model="defaultConfigForm.username"
                            :state="defaultConfigFieldState(defaultConfigForm.username)" />
                        <b-form-invalid-feedback>Username is required.</b-form-invalid-feedback>
                    </b-form-group>

                    <template v-if="defaultConfigForm.authType === 'Password' || defaultConfigForm.authType === 'KeyboardInteractive'">
                        <b-form-group label="Password" label-cols="3">
                            <b-form-input v-model="defaultConfigForm.password" type="password"
                                placeholder="Enter password" />
                        </b-form-group>
                    </template>

                    <template v-if="defaultConfigForm.authType === 'PrivateKey'">
                        <b-form-group label="Private Key" label-cols="3">
                            <b-form-textarea v-model="defaultConfigForm.privateKey" rows="4"
                                placeholder="Paste SSH private key content" />
                        </b-form-group>
                        <b-form-group label="Passphrase" label-cols="3">
                            <b-form-input v-model="defaultConfigForm.passphrase" type="password"
                                placeholder="Enter passphrase (if required)" />
                        </b-form-group>
                    </template>

                    <b-form-group label-cols="3">
                        <b-button size="sm" variant="secondary" @click="testDefaultConnection" :disabled="testingDefaultConnection">
                            {{ testingDefaultConnection ? 'Testing...' : 'Test Connection' }}
                        </b-button>
                        <b-alert
                            v-if="defaultTestResult !== null"
                            show
                            :variant="defaultTestResult.success ? 'success' : 'danger'"
                            dismissible
                            class="mt-2 mb-0"
                            @dismissed="defaultTestResult = null"
                        >
                            {{ defaultTestResult.message }}
                        </b-alert>
                    </b-form-group>
                </template>

                <hr />
                <b-form-group label="Notes" label-cols="3">
                    <b-form-textarea v-model="defaultConfigForm.notes" rows="3"
                        placeholder="e.g., credential expiration dates, state contacts, special requirements" />
                </b-form-group>
            </b-form>
            <div class="d-flex justify-content-between align-items-center mt-3">
                <b-link @click="openDefaultConfigHistory">
                    <i class="fa fa-history" /> Change History
                </b-link>
                <b-button variant="primary" @click="saveDefaultConfig" :disabled="savingConfig">Save</b-button>
            </div>
        </b-modal>

        <!-- Submission History Modal -->
        <b-modal v-model="showHistory" :title="'Submission History: ' + historyState" size="xl" no-fade hide-footer>
            <b-table :items="submissionHistory" :fields="historyFields" striped responsive show-empty
                empty-text="No submission history found.">
                <template #cell(status)="data">
                    <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
                </template>
                <template #cell(submittedDate)="data">
                    {{ formatDate(data.value) }}
                </template>
            </b-table>
        </b-modal>

        <!-- SFTP Config Change History Modal -->
        <b-modal v-model="showSftpConfigHistory" :title="'Configuration History: ' + editingState" size="lg" no-fade
            hide-footer no-close-on-backdrop>
            <b-table :items="sftpConfigHistory" :fields="configHistoryFields" striped responsive
                show-empty empty-text="No change history." small>
                <template #cell(updated)="data">
                    {{ formatDate(data.value) }}
                </template>
                <template #cell(changes)="data">
                    <span v-for="(change, idx) in getChangedFields(data.item)" :key="idx">
                        <strong>{{ change.field }}</strong><template v-if="change.oldVal !== '' || change.newVal !== ''">: {{ change.oldVal }} → {{ change.newVal }}</template>
                        <br v-if="idx < getChangedFields(data.item).length - 1" />
                    </span>
                </template>
            </b-table>
        </b-modal>

        <!-- Default Clearinghouse Change History Modal -->
        <b-modal v-model="showDefaultConfigHistory" title="Default Clearinghouse History" size="lg" no-fade
            hide-footer no-close-on-backdrop>
            <b-table :items="defaultConfigHistory" :fields="configHistoryFields" striped responsive
                show-empty empty-text="No change history." small>
                <template #cell(updated)="data">
                    {{ formatDate(data.value) }}
                </template>
                <template #cell(changes)="data">
                    <span v-for="(change, idx) in getChangedFields(data.item)" :key="idx">
                        <strong>{{ change.field }}</strong><template v-if="change.oldVal !== '' || change.newVal !== ''">: {{ change.oldVal }} → {{ change.newVal }}</template>
                        <br v-if="idx < getChangedFields(data.item).length - 1" />
                    </span>
                </template>
            </b-table>
        </b-modal>
    </card>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import HelpPopup from '@/components/HelpPopup.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { History, historyDiff } from '@/models/History';

interface PmpStatusRow {
    stateID: string;
    frequency: string;
    dateSubmitted: string;
    data?: string;
    status?: string;
    errorMessage?: string;
    configSource?: string;
    configId?: number;
    isActive?: boolean;
}

interface SftpConfigResponse {
    id: number;
    stateID: string;
    host?: string;
    port: number;
    authType?: string;
    username?: string;
    fileLocation?: string;
    isActive: boolean;
    credentialSource: string;
    notes?: string;
    hasPassword: boolean;
    hasPrivateKey: boolean;
}

interface SftpConfigForm {
    stateID: string;
    host: string;
    port: number;
    authType: string;
    username: string;
    password: string;
    privateKey: string;
    passphrase: string;
    fileLocation: string;
    isActive: boolean;
    credentialSource: string;
    notes: string;
}

interface DefaultConfigForm {
    credentialSource: string;
    host: string;
    port: number;
    authType: string;
    username: string;
    password: string;
    privateKey: string;
    passphrase: string;
    notes: string;
}

interface SubmissionLog {
    submissionId: number;
    stateID: string;
    storeId?: number;
    submittedDate: string;
    status: string;
    errorMessage?: string;
    itemCount: number;
    fileName?: string;
}

@Component({ name: "PMPStatusPage", components: { HelpPopup } })
export default class PMPStatusPage extends Vue {
    private pmpstatus: PmpStatusRow[] = [];
    private sftpConfigs: SftpConfigResponse[] = [];
    private latestSubmissions: SubmissionLog[] = [];
    private submissionHistory: SubmissionLog[] = [];
    private stores: { id: number; name: string }[] = [];
    private selectedStoreId: number | null = null;

    private showConfigModal = false;
    private showDefaultConfig = false;
    private showHistory = false;
    private savingConfig = false;
    private testingConnection = false;
    private testResult: { success: boolean; message: string } | null = null;
    private testingDefaultConnection = false;
    private defaultTestResult: { success: boolean; message: string } | null = null;
    private defaultConfigExists = false;
    private configFormValidated = false;
    private defaultConfigFormValidated = false;
    private showDefaultConfigHistory = false;
    private defaultConfigHistory: History[] = [];
    private showSftpConfigHistory = false;
    private sftpConfigHistory: History[] = [];

    private editingState = '';
    private editingConfigId: number | null = null;
    private historyState = '';

    private configForm: SftpConfigForm = this.emptyConfigForm();
    private defaultConfigForm: DefaultConfigForm = this.emptyDefaultConfigForm();

    private filterFrequency = '';
    private filterLastResult = '';
    private filterConfigType = '';
    private filterStatus = '';

    private frequencyOptions = [
        { value: '', text: 'All Frequencies' },
        { value: 'Daily', text: 'Daily' },
        { value: 'Weekly', text: 'Weekly' },
        { value: 'Monthly', text: 'Monthly' }
    ];

    private lastResultOptions = [
        { value: '', text: 'All Results' },
        { value: '_submissionFailed', text: 'All Submission Failures' },
        { value: '_authFailed', text: 'All Credential Issues' },
        { value: 'Success', text: 'Success' },
        { value: 'AuthenticationFailed', text: 'Auth Failed' },
        { value: 'ConnectionFailed', text: 'Connection Failed' },
        { value: 'Timeout', text: 'Timeout' },
        { value: 'Failed', text: 'Failed' }
    ];

    private configTypeOptions = [
        { value: '', text: 'All Config Types' },
        { value: 'Clearinghouse', text: 'Clearinghouse' },
        { value: 'Scriptly', text: 'Scriptly' },
        { value: 'Custom', text: 'Custom' },
        { value: 'Legacy', text: 'Legacy' }
    ];

    private statusOptions = [
        { value: '', text: 'All Statuses' },
        { value: 'enabled', text: 'Enabled' },
        { value: 'disabled', text: 'Disabled' }
    ];

    private authTypes = [
        { value: 'Password', text: 'Password' },
        { value: 'PrivateKey', text: 'Private Key' },
        { value: 'KeyboardInteractive', text: 'Keyboard Interactive' }
    ];

    private pmpstatusFields = [
        { key: 'stateID', label: 'State', sortable: true },
        { key: 'frequency', label: 'Frequency', sortable: true },
        { key: 'dateSubmitted', label: 'Last Submission', sortable: true },
        { key: 'status', label: 'Last Result', sortable: true },
        { key: 'configSource', label: 'Config', sortable: true },
        { key: 'isActive', label: 'Status', sortable: true },
        { key: 'actions', label: '', tdClass: 'text-right' }
    ];

    private configHistoryFields = [
        { key: 'updated', label: 'Date', sortable: true },
        { key: 'updatedBy', label: 'Changed By' },
        { key: 'changes', label: 'Changes' }
    ];

    private historyFields = [
        { key: 'submittedDate', label: 'Date', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'itemCount', label: 'Items' },
        { key: 'fileName', label: 'File' },
        { key: 'errorMessage', label: 'Error' }
    ];

    get canConfigurePMP(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Admin/PMP Configuration');
    }

    get isConfigFormValid(): boolean {
        if (this.configForm.credentialSource !== 'Custom') return true;
        return !!this.configForm.host.trim() && !!this.configForm.username.trim();
    }

    get isDefaultConfigFormValid(): boolean {
        if (this.defaultConfigForm.credentialSource === 'ScriptlyManaged') return true;
        return !!this.defaultConfigForm.host.trim() && !!this.defaultConfigForm.username.trim();
    }

    get storeOptions() {
        return [
            { value: null, text: 'All stores' },
            ...this.stores.map(s => ({ value: s.id, text: s.name }))
        ];
    }

    private configFieldState(value: string): boolean | null {
        if (!this.configFormValidated) return null;
        return !!value.trim();
    }

    private defaultConfigFieldState(value: string): boolean | null {
        if (!this.defaultConfigFormValidated) return null;
        return !!value.trim();
    }

    get mergedStatus(): PmpStatusRow[] {
        return this.pmpstatus.map(s => {
            const config = this.sftpConfigs.find(
                c => c.stateID?.toUpperCase() === s.stateID?.toUpperCase()
            );
            // Find all submissions for this state (may be multiple stores)
            const stateSubmissions = this.latestSubmissions.filter(
                l => l.stateID?.toUpperCase() === s.stateID?.toUpperCase()
            );
            // Show worst status: any failure takes precedence over success
            const worstSubmission = stateSubmissions.find(l => l.status !== 'Success') ?? stateSubmissions[0];
            return {
                ...s,
                status: worstSubmission?.status,
                errorMessage: worstSubmission?.errorMessage,
                configSource: config
                    ? { DefaultClearinghouse: 'Clearinghouse', ScriptlyManaged: 'Scriptly', Custom: 'Custom' }[config.credentialSource] ?? config.credentialSource
                    : 'Legacy',
                configId: config?.id,
                isActive: config?.isActive ?? true
            };
        });
    }

    get filteredStatus(): PmpStatusRow[] {
        return this.mergedStatus.filter(s =>
            (!this.filterFrequency || s.frequency === this.filterFrequency) &&
            (!this.filterLastResult || this.matchesResultFilter(s.status)) &&
            (!this.filterConfigType || s.configSource === this.filterConfigType) &&
            (!this.filterStatus || (this.filterStatus === 'enabled') === s.isActive)
        );
    }

    private matchesResultFilter(status?: string): boolean {
        if (this.filterLastResult === '_submissionFailed')
            return !!status && status !== 'Success' && status !== 'AuthenticationFailed';
        if (this.filterLastResult === '_authFailed')
            return status === 'AuthenticationFailed';
        return status === this.filterLastResult;
    }

    get submissionFailedStates(): string[] {
        return this.mergedStatus
            .filter(s => s.status && s.status !== 'Success' && s.status !== 'AuthenticationFailed')
            .map(s => s.stateID);
    }

    get authFailedStates(): string[] {
        return this.mergedStatus
            .filter(s => s.status === 'AuthenticationFailed')
            .map(s => s.stateID);
    }

    get alertCount(): number {
        return (this.submissionFailedStates.length > 0 ? 1 : 0) + (this.authFailedStates.length > 0 ? 1 : 0);
    }

    created() {
        this.loadData();
    }

    private async loadData() {
        await Promise.all([
            this.getStores(),
            this.getPMPStatus(),
            this.getSftpConfigs(),
            this.getLatestSubmissions(),
            this.getDefaultClearinghouseConfig()
        ]);
    }

    private async onStoreFilterChanged() {
        await Promise.all([
            this.getPMPStatus(),
            this.getLatestSubmissions()
        ]);
    }

    private async getStores() {
        try {
            const response = await axios.get('/Prescription/pmpstores');
            this.stores = response.data;
        } catch (error) {
            console.error('Error loading PMP stores', error);
        }
    }

    private storeIdParams() {
        return this.selectedStoreId != null ? { params: { storeId: this.selectedStoreId } } : undefined;
    }

    private async getPMPStatus() {
        try {
            const response = await axios.get('/Prescription/pmpstatus', this.storeIdParams());
            this.pmpstatus = response.data;
        } catch (error: any) {
            console.error('Error while getting PMP Status reports', { error, response: error?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    private async getSftpConfigs() {
        try {
            const response = await axios.get('/PmpConfig/sftp-configs');
            this.sftpConfigs = response.data;
        } catch (error) {
            console.error('Error loading SFTP configs', error);
        }
    }

    private async getLatestSubmissions() {
        try {
            const response = await axios.get('/PmpConfig/submission-logs/latest', this.storeIdParams());
            this.latestSubmissions = response.data;
        } catch (error) {
            console.error('Error loading latest submissions', error);
        }
    }

    private async getDefaultClearinghouseConfig() {
        try {
            const response = await axios.get('/PmpConfig/default-clearinghouse');
            if (response.data) {
                this.defaultConfigExists = true;
                this.defaultConfigForm = {
                    credentialSource: response.data.credentialSource || 'Custom',
                    host: response.data.host || '',
                    port: response.data.port || 22,
                    authType: response.data.authType || 'Password',
                    username: response.data.username || '',
                    password: '',
                    privateKey: '',
                    passphrase: '',
                    notes: response.data.notes || ''
                };
            }
        } catch (error) {
            console.error('Error loading default clearinghouse config', error);
        }
    }

    private onRowClicked(item: PmpStatusRow) {
        this.viewHistory(item);
    }

    private editConfig(item: PmpStatusRow) {
        this.editingState = item.stateID;
        this.testResult = null;
        this.configFormValidated = false;
        this.showSftpConfigHistory = false;
        this.sftpConfigHistory = [];

        const existing = this.sftpConfigs.find(
            c => c.stateID?.toUpperCase() === item.stateID?.toUpperCase()
        );

        if (existing) {
            this.editingConfigId = existing.id;
            this.configForm = {
                stateID: existing.stateID,
                host: existing.host || '',
                port: existing.port,
                authType: existing.authType || 'Password',
                username: existing.username || '',
                password: '',
                privateKey: '',
                passphrase: '',
                fileLocation: existing.fileLocation || '',
                isActive: existing.isActive,
                credentialSource: existing.credentialSource || 'DefaultClearinghouse',
                notes: existing.notes || ''
            };
        } else {
            this.editingConfigId = null;
            this.configForm = this.emptyConfigForm();
            this.configForm.stateID = item.stateID;
        }

        this.showConfigModal = true;
    }

    private async saveConfig() {
        this.configFormValidated = true;
        if (!this.isConfigFormValid) return;
        this.savingConfig = true;
        try {
            const payload = { ...this.configForm };
            if (this.editingConfigId) {
                await axios.put(`/PmpConfig/sftp-configs/${this.editingConfigId}`, payload);
            } else {
                await axios.post('/PmpConfig/sftp-configs', payload);
            }
            this.$notification(NotificationOptions.successSaveNotificationPreset('SFTP Configuration'));
            this.showConfigModal = false;
            this.sftpConfigHistory = [];
            this.showSftpConfigHistory = false;
            await this.getSftpConfigs();
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.savingConfig = false;
        }
    }

    private openDefaultConfig() {
        this.defaultConfigFormValidated = false;
        this.defaultTestResult = null;
        this.showDefaultConfig = true;
    }

    private async saveDefaultConfig() {
        this.defaultConfigFormValidated = true;
        if (!this.isDefaultConfigFormValid) return;
        this.savingConfig = true;
        try {
            await axios.post('/PmpConfig/default-clearinghouse', this.defaultConfigForm);
            this.$notification(NotificationOptions.successSaveNotificationPreset('Default Clearinghouse'));
            this.showDefaultConfig = false;
            this.defaultConfigExists = true;
            this.defaultConfigHistory = [];
            this.showDefaultConfigHistory = false;
        } catch (error: any) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.savingConfig = false;
        }
    }

    private async testConnection() {
        this.testingConnection = true;
        this.testResult = null;
        try {
            const response = await axios.post('/PmpConfig/test-sftp', this.configForm);
            this.testResult = response.data;
        } catch (error: any) {
            this.testResult = { success: false, message: 'Request failed: ' + (error?.message || 'Unknown error') };
        } finally {
            this.testingConnection = false;
        }
    }

    private filterBySubmissionFailures() {
        this.filterLastResult = this.filterLastResult === '_submissionFailed' ? '' : '_submissionFailed';
    }

    private filterByAuthFailures() {
        this.filterLastResult = this.filterLastResult === '_authFailed' ? '' : '_authFailed';
    }

    private async testDefaultConnection() {
        this.testingDefaultConnection = true;
        this.defaultTestResult = null;
        try {
            const response = await axios.post('/PmpConfig/test-sftp', this.defaultConfigForm);
            this.defaultTestResult = response.data;
        } catch (error: any) {
            this.defaultTestResult = { success: false, message: 'Request failed: ' + (error?.message || 'Unknown error') };
        } finally {
            this.testingDefaultConnection = false;
        }
    }

    private async openDefaultConfigHistory() {
        this.showDefaultConfigHistory = true;
        try {
            const response = await axios.get('/PmpConfig/default-clearinghouse/history');
            this.defaultConfigHistory = response.data;
        } catch (error) {
            console.error('Error loading default config history', error);
        }
    }

    private async openSftpConfigHistory() {
        if (!this.editingConfigId) return;
        this.showSftpConfigHistory = true;
        try {
            const response = await axios.get(`/PmpConfig/sftp-configs/${this.editingConfigId}/history`);
            this.sftpConfigHistory = response.data;
        } catch (error) {
            console.error('Error loading SFTP config history', error);
        }
    }

    private getChangedFields(item: History): { field: string; oldVal: string; newVal: string }[] {
        try {
            const delta = historyDiff(item);
            if (!delta) return [{ field: '(no changes)', oldVal: '', newVal: '' }];

            // jsondiffpatch encodes a root-level add (first-ever save, no prior state)
            // as a single-element array [newValueObj], which has no per-field diffs to list.
            if (Array.isArray(delta)) return [{ field: 'Initial', oldVal: '', newVal: '' }];

            return Object.keys(delta)
                .filter(key => !key.startsWith('_') && key !== 'ModifiedDate' && key !== 'ModifiedBy')
                .map(key => {
                    const change = delta[key];
                    if (Array.isArray(change)) {
                        if (change.length === 2) return { field: key, oldVal: String(change[0] ?? ''), newVal: String(change[1] ?? '') };
                        if (change.length === 1) return { field: key, oldVal: '(none)', newVal: String(change[0] ?? '') };
                    }
                    return { field: key, oldVal: '?', newVal: '?' };
                });
        } catch {
            return [{ field: '(unable to parse changes)', oldVal: '', newVal: '' }];
        }
    }

    private formatDate(date: string | Date): string {
        if (!date) return '';
        return new Date(date).toLocaleString();
    }

    private async viewHistory(item: PmpStatusRow) {
        this.historyState = item.stateID;
        this.showHistory = true;
        try {
            const response = await axios.get(`/PmpConfig/submission-logs/${item.stateID}`, this.storeIdParams());
            this.submissionHistory = response.data;
        } catch (error) {
            console.error('Error loading submission history', error);
            this.submissionHistory = [];
        }
    }

    private async onKeyFileSelected(file: File | null) {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            this.configForm.privateKey = e.target?.result as string || '';
        };
        reader.readAsText(file);
    }

    private statusVariant(status: string): string {
        switch (status) {
            case 'Success': return 'success';
            case 'AuthenticationFailed': return 'danger';
            case 'ConnectionFailed': return 'warning';
            case 'Timeout': return 'warning';
            case 'Failed': return 'danger';
            default: return 'secondary';
        }
    }

    private emptyConfigForm(): SftpConfigForm {
        return {
            stateID: '',
            host: '',
            port: 22,
            authType: 'Password',
            username: '',
            password: '',
            privateKey: '',
            passphrase: '',
            fileLocation: '',
            isActive: true,
            credentialSource: 'DefaultClearinghouse',
            notes: ''
        };
    }

    private emptyDefaultConfigForm(): DefaultConfigForm {
        return {
            credentialSource: 'ScriptlyManaged',
            host: '',
            port: 22,
            authType: 'Password',
            username: '',
            password: '',
            privateKey: '',
            passphrase: '',
            notes: ''
        };
    }
}
</script>

<style scoped>
.filters {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 8px;
    margin-bottom: 15px;
}

.filters > * {
    margin-bottom: 0;
}

.clickable-alert {
    cursor: pointer;
}

.clickable-alert:hover {
    opacity: 0.9;
}

.alert-icon i {
    background: white;
    width: 2em;
    height: 2em;
    border-radius: 50%;
    margin-right: 12px;
    box-shadow: 0 0 6px rgba(0, 0, 0, .25);
    text-align: center;
    font-size: 1.2em;
    line-height: 2;
    display: inline-block;
}

.alert-danger .alert-icon i {
    color: #dc3545;
}

.alert-warning .alert-icon i {
    color: #ffc107;
}

.pmp-alerts {
    display: grid;
    gap: 7px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 520px), 1fr));
}

.pmp-alerts[data-alert-count='1'] {
    grid-template-columns: 1fr;
}

@media (min-width: 1100px) {
    .pmp-alerts[data-alert-count='2'] {
        grid-template-columns: 1fr 1fr;
    }
}

</style>
